/* -----------------Globals--------------- */
import { useCallback } from "react";
import type { ViewStyle } from "react-native";

import Box from "../Box";
/* -----------------UI--------------- */
import { FadeView } from "../FadeView";
import FollowUpMessage from "../FollowUpMessageList/FollowUpMessage";

/* -----------------Child components--------------- */
import { ScrollButtonFlatlist } from "./ScrollButtonFlatlist";

/* -----------------Types--------------- */
import type { IconType } from "../TextLoop";

type RecommendedChipsProps = {
	queries: string[];
	handleSelectQuery: (query: string) => void;
	showCustomResponse?: boolean;
	customResponseLabel?: string;
	customResponseIcon?: IconType;
	defaultIcon?: IconType;
	showSummarizeResponse?: boolean;
	containerProps?: ViewStyle;
	noFlatList?: boolean;
};

export const RecommendedChips = ({
	queries,
	handleSelectQuery,
	showCustomResponse = false,
	customResponseLabel = "Ask your own question",
	customResponseIcon = "chat-question-outline",
	defaultIcon = "arrow-right-bottom",
	showSummarizeResponse,
	containerProps,
	noFlatList = false,
}: RecommendedChipsProps) => {
	const DATA: {
		key: string;
		query: string;
		label: string;
		icon?: IconType;
		type: "starterMessage" | "default" | "custom";
	}[] = [
		...(showCustomResponse
			? [
					{
						key: "custom",
						query: "",
						label: customResponseLabel,
						icon: customResponseIcon,
						type: "custom" as const,
					},
				]
			: []),
		...queries.map((query) => ({
			key: query,
			query,
			label: query,
			icon: defaultIcon,
			type: "default" as const,
		})),
		...(showSummarizeResponse
			? [
					{
						key: "summarize",
						query: "Summarize this page",
						label: "Summarize this page",
						icon: customResponseIcon,
						type: "custom" as const,
					},
				]
			: []),
	];

	const renderChip = useCallback(
		({ query, label, icon, type, key }: (typeof DATA)[number]) => (
			<FadeView duration={500} key={key}>
				<FollowUpMessage
					message={label}
					icon={icon}
					type={type}
					onPress={() => handleSelectQuery(query)}
					style={{ alignSelf: "flex-start" }}
				/>
			</FadeView>
		),
		[handleSelectQuery],
	);

	if (noFlatList) {
		return (
			<Box gap={12} width="100%" style={containerProps}>
				{DATA.map((chipData) => renderChip(chipData))}
			</Box>
		);
	}

	return (
		<ScrollButtonFlatlist
			data={DATA}
			contentContainerStyle={[
				{
					gap: 12,
				},
				containerProps,
			]}
			keyExtractor={(item) => item.key}
			renderItem={({ item }) => renderChip(item)}
		/>
	);
};
